import type { ChartOption } from "./chart-option.js"
import { ink, ordinalColor, surface, type ThemeMode } from "../palette.js"
import { formatValue } from "../format.js"
import type { VizModel } from "../tile-model.js"
import { tooltipFor } from "./base.js"

export type PieOptions = {
  mode: ThemeMode
  model: VizModel
  donut?: boolean
  centerLabel?: string
  legendPosition?: "bottom" | "right"
}

export function pieOption({
  mode,
  model,
  donut = true,
  centerLabel = "Total",
  legendPosition = "right",
}: PieOptions): ChartOption {
  const c = ink(mode)
  const first = model.series[0]

  const data = model.categories.map((name, i) => ({
    name,
    value: first?.data[i] ?? null,
    itemStyle: {
      color: ordinalColor(i, mode),
      borderColor: surface(mode),
      borderWidth: 2,
    },
  }))

  const total = data.reduce((sum, d) => sum + (typeof d.value === "number" ? d.value : 0), 0)

  return {
    backgroundColor: "transparent",
    animationDuration: 240,
    textStyle: { color: c.primary },
    tooltip: tooltipFor(mode, model, "item"),
    legend: {
      type: "scroll",
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { color: c.secondary, fontSize: 12 },
      ...(legendPosition === "right"
        ? { orient: "vertical" as const, right: 0, top: "middle" as const }
        : { orient: "horizontal" as const, top: 4, left: "center" as const }),
    },
    series: [
      {
        type: "pie",
        radius: donut ? ["52%", "76%"] : "76%",
        center: ["38%", "50%"],
        avoidLabelOverlap: true,
        label: {
          show: true,
          color: c.secondary,
          fontSize: 11,
          formatter: (p: { name: string; percent?: number }) =>
            `${p.name}\n${(p.percent ?? 0).toFixed(1)}%`,
        },
        labelLine: { length: 8, length2: 8, lineStyle: { color: c.baseline } },
        emphasis: { itemStyle: { opacity: 0.85 } },
        data,
        ...(donut
          ? {
              emptyCircleStyle: { color: c.gridline },
            }
          : {}),
      },
    ],
    ...(donut
      ? {
          graphic: [
            {
              type: "text",
              left: "38%",
              top: "middle",
              style: {
                text: `${formatValue(total, { valueType: model.unit })}\n${centerLabel}`,
                textAlign: "center",
                fill: c.primary,
                fontSize: 18,
                fontWeight: 600,
                lineHeight: 22,
              },
            },
          ],
        }
      : {}),
  }
}
